import { Mark, mergeAttributes } from '@tiptap/core';

import { ATTRIBUTES } from '../_constants';

export const DEFAULT_FONT_SIZE = 'typo-body3';

export const FONT_SIZES = [
  'typo-h4',
  'typo-h5',
  'typo-h6',
  'typo-body1',
  'typo-body2',
  DEFAULT_FONT_SIZE,
  'typo-body4',
] as const;

export const BIZ_FONT_SIZES = [
  'biz-h2',
  'biz-h3',
  'typo-body1',
  'typo-body2',
  DEFAULT_FONT_SIZE,
  'typo-body4',
] as const;

export type FontSizeType = (typeof FONT_SIZES)[number];
export type BizFontSizeType = (typeof BIZ_FONT_SIZES)[number];

interface FontSizeOptions {
  HTMLAttributes: Record<string, unknown>;
}

interface FontSizeAttributes {
  fontSize: FontSizeType | null;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    customFontSize: {
      setBizFontSize: (bizFontSize: BizFontSizeType) => ReturnType;
    };
  }
}

export const FontSize = Mark.create<FontSizeOptions>({
  name: 'fontSize',

  addOptions() {
    //* 기본 옵션을 정의
    return {
      HTMLAttributes: {},
    };
  },

  //* 노드가 가질 수 있는 속성을 추가합니다.
  addAttributes() {
    return {
      fontSize: {
        //* 기본 폰트 사이즈 설정
        default: DEFAULT_FONT_SIZE satisfies FontSizeType,
        parseHTML: (element: HTMLElement) =>
          element.getAttribute(ATTRIBUTES.FONT_SIZE),
        renderHTML: (attributes: FontSizeAttributes) => {
          if (!attributes.fontSize) {
            return {};
          }

          return {
            [ATTRIBUTES.FONT_SIZE]: attributes.fontSize,
            class: attributes.fontSize,
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: `span[${ATTRIBUTES.FONT_SIZE}]`,
      },
    ];
  },

  renderHTML({ HTMLAttributes }: FontSizeOptions) {
    return [
      'span',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ];
  },

  addKeyboardShortcuts() {
    return {
      //* heading인 경우 엔터키를 입력했을 때 기본 fontSize로 변경
      Enter: ({ editor }) => {
        const { state, commands } = editor;

        const { $from } = state.selection;
        const nodeType = $from.parent.type.name;

        //* 만약 현재 노드가 heading이라면 기본 폰트 사이즈로 변경
        if (nodeType.startsWith('heading')) {
          commands.setMark(this.name, { fontSize: DEFAULT_FONT_SIZE });
        }

        return false; //* 기본 엔터 키 동작을 유지
      },
    };
  },

  addCommands() {
    return {
      setFontSize:
        (fontSize: string) =>
        ({ chain }) => {
          let changedCommand = chain();

          if (fontSize.startsWith('typo-h')) {
            const level = Number(fontSize.replace('typo-h', '')) as 4 | 5 | 6;

            changedCommand = changedCommand.setHeading({ level });
          }

          return changedCommand.setMark(this.name, { fontSize }).run();
        },
      setBizFontSize:
        (bizFontSize: BizFontSizeType) =>
        ({ chain }) => {
          if (bizFontSize === 'biz-h2') {
            return chain()
              .setHeading({ level: 2 })
              .setMark(this.name, { fontSize: bizFontSize })
              .run();
          }

          if (bizFontSize === 'biz-h3') {
            return chain()
              .setHeading({ level: 3 })
              .setMark(this.name, { fontSize: bizFontSize })
              .run();
          }

          return chain().setMark(this.name, { fontSize: bizFontSize }).run();
        },
    };
  },
});
